import { apiSlice } from "../../../features/api/apiSlice";

/**
 * HR: secretariat staff, attendance and leave.
 *
 * Staff records ARE the `admins` registry — HR reads them and owns only two
 * fields on each (the biometric enrolment id used to match device imports, and
 * the work window lateness/hours are measured against). Creating or editing the
 * person happens under Admin management.
 */

/** Laravel paginator envelope → the rows. */
const rows = (res: any): any[] =>
  Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

/** Laravel paginator envelope → the meta a table needs. */
const meta = (res: any) => ({
  total: res?.total ?? res?.data?.total ?? 0,
  per_page: res?.per_page ?? res?.data?.per_page ?? 0,
  current_page: res?.current_page ?? res?.data?.current_page ?? 1,
  last_page: res?.last_page ?? res?.data?.last_page ?? 1,
});

export type HrPaged = { rows: any[]; meta: ReturnType<typeof meta> };

const qs = (params: Record<string, any>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const hrApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ── Staff ─────────────────────────────────────────────────────────── */

    hrGetStaff: builder.query<
      HrPaged,
      { search?: string; status?: string; department_id?: string | number; page?: number; per_page?: number } | void
    >({
      query: (arg) => `/hr/staff${qs({ per_page: 200, ...(arg ?? {}) })}`,
      transformResponse: (res: any) => ({ rows: rows(res), meta: meta(res) }),
      providesTags: [{ type: "HrStaff" as const, id: "LIST" }],
    }),

    hrGetStaffMember: builder.query<any, string | number>({
      query: (id) => `/hr/staff/${id}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: (_r, _e, id) => [{ type: "HrStaffDetails" as const, id: String(id) }],
    }),

    // POST rather than PATCH — some shared hosts block PATCH (the API accepts both).
    hrUpdateStaffFields: builder.mutation<
      any,
      { id: string | number; body: { biometric_id?: string | null; work_start?: string | null; work_end?: string | null } }
    >({
      query: ({ id, body }) => ({ url: `/hr/staff/${id}/hr`, method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "HrStaff" as const, id: "LIST" },
        { type: "HrStaffDetails" as const, id: String(arg.id) },
      ],
    }),

    /* ── Attendance ────────────────────────────────────────────────────── */

    hrGetAttendance: builder.query<
      HrPaged,
      { date?: string; from?: string; to?: string; admin_id?: string | number; status?: string; per_page?: number } | void
    >({
      query: (arg) => `/hr/attendance${qs({ per_page: 500, ...(arg ?? {}) })}`,
      transformResponse: (res: any) => ({ rows: rows(res), meta: meta(res) }),
      providesTags: [{ type: "HrAttendance" as const, id: "LIST" }],
    }),

    hrGetAttendanceSummary: builder.query<any[], { from: string; to: string; admin_id?: string | number }>({
      query: (arg) => `/hr/attendance/summary${qs(arg)}`,
      transformResponse: (res: any) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "HrAttendanceSummary" as const, id: "LIST" }],
    }),

    /** Upload the biometric device's CSV export. */
    hrImportAttendance: builder.mutation<any, FormData>({
      query: (body) => ({ url: "/hr/attendance/import", method: "POST", body }),
      invalidatesTags: [
        { type: "HrAttendance" as const, id: "LIST" },
        { type: "HrAttendanceSummary" as const, id: "LIST" },
      ],
    }),

    /** Record or correct one staff member's day by hand. */
    hrSaveAttendance: builder.mutation<
      any,
      {
        admin_id: string | number;
        work_date: string;
        clock_in?: string | null;
        clock_out?: string | null;
        status?: string | null;
        note?: string | null;
      }
    >({
      query: (body) => ({ url: "/hr/attendance", method: "POST", body }),
      invalidatesTags: [
        { type: "HrAttendance" as const, id: "LIST" },
        { type: "HrAttendanceSummary" as const, id: "LIST" },
      ],
    }),

    /* ── Leave types ───────────────────────────────────────────────────── */

    hrGetLeaveTypes: builder.query<any[], { all?: boolean } | void>({
      query: (arg) => `/hr/leave/types${arg && (arg as any).all ? "?all=1" : ""}`,
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
      providesTags: [{ type: "HrLeaveTypes" as const, id: "LIST" }],
    }),

    hrCreateLeaveType: builder.mutation<any, any>({
      query: (body) => ({ url: "/hr/leave/types", method: "POST", body }),
      invalidatesTags: [{ type: "HrLeaveTypes" as const, id: "LIST" }],
    }),

    hrUpdateLeaveType: builder.mutation<any, { id: string | number; body: any }>({
      query: ({ id, body }) => ({ url: `/hr/leave/types/${id}`, method: "PUT", body }),
      invalidatesTags: [{ type: "HrLeaveTypes" as const, id: "LIST" }],
    }),

    /* ── Leave requests ────────────────────────────────────────────────── */

    hrGetLeaveRequests: builder.query<
      HrPaged,
      { status?: string; admin_id?: string | number; per_page?: number } | void
    >({
      query: (arg) => `/hr/leave/requests${qs({ per_page: 200, ...(arg ?? {}) })}`,
      transformResponse: (res: any) => ({ rows: rows(res), meta: meta(res) }),
      providesTags: [{ type: "HrLeaveRequests" as const, id: "LIST" }],
    }),

    hrCreateLeaveRequest: builder.mutation<
      any,
      { admin_id: string | number; leave_type_id: string | number; start_date: string; end_date: string; reason?: string }
    >({
      query: (body) => ({ url: "/hr/leave/requests", method: "POST", body }),
      invalidatesTags: [
        { type: "HrLeaveRequests" as const, id: "LIST" },
        { type: "HrLeaveBalances" as const, id: "LIST" },
      ],
    }),

    // Approving also writes the days into the attendance register.
    hrApproveLeave: builder.mutation<any, { id: string | number; note?: string }>({
      query: ({ id, note }) => ({ url: `/hr/leave/requests/${id}/approve`, method: "POST", body: { note } }),
      invalidatesTags: [
        { type: "HrLeaveRequests" as const, id: "LIST" },
        { type: "HrLeaveBalances" as const, id: "LIST" },
        { type: "HrAttendance" as const, id: "LIST" },
        { type: "HrAttendanceSummary" as const, id: "LIST" },
      ],
    }),

    hrRejectLeave: builder.mutation<any, { id: string | number; note: string }>({
      query: ({ id, note }) => ({ url: `/hr/leave/requests/${id}/reject`, method: "POST", body: { note } }),
      invalidatesTags: [{ type: "HrLeaveRequests" as const, id: "LIST" }],
    }),

    /* ── Leave balances ────────────────────────────────────────────────── */

    hrGetLeaveBalances: builder.query<any[], { year?: number; admin_id?: string | number } | void>({
      query: (arg) => `/hr/leave/balances${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "HrLeaveBalances" as const, id: "LIST" }],
    }),

    hrSetLeaveBalance: builder.mutation<
      any,
      { admin_id: string | number; leave_type_id: string | number; year: number; entitled_days: number }
    >({
      query: (body) => ({ url: "/hr/leave/balances", method: "POST", body }),
      invalidatesTags: [{ type: "HrLeaveBalances" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useHrGetStaffQuery,
  useHrGetStaffMemberQuery,
  useHrUpdateStaffFieldsMutation,
  useHrGetAttendanceQuery,
  useHrGetAttendanceSummaryQuery,
  useHrImportAttendanceMutation,
  useHrSaveAttendanceMutation,
  useHrGetLeaveTypesQuery,
  useHrCreateLeaveTypeMutation,
  useHrUpdateLeaveTypeMutation,
  useHrGetLeaveRequestsQuery,
  useHrCreateLeaveRequestMutation,
  useHrApproveLeaveMutation,
  useHrRejectLeaveMutation,
  useHrGetLeaveBalancesQuery,
  useHrSetLeaveBalanceMutation,
} = hrApi;
